<template>
  <div class="mapContenedor">
    <GMapMap
      ref="mapRef"
      :center="currentCenter"
      :zoom="14"
      map-type-id="terrain"
      class="mapa"
      :options="{
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUI: false,
        clickableIcons: false,
        draggableCursor: readOnly ? 'default' : null
      }"
      @click="handleMapClick"
    >
      <GMapMarker
        :position="markerPosition"
        :clickable="!readOnly"
        :draggable="!readOnly" 
        @dragend="updateMarkerPosition"
      />
    </GMapMap>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
  setTextInput: { type: Function, default: null },
  positionReceived: { type: String, default: null },
  center: {
    type: Object,
    default: () => ({ lat: -31.6476596, lng: -63.3449318 })
  },
  readOnly: { 
    type: Boolean, 
    default: false 
  }
});

const mapRef = ref(null);
const isInternalChange = ref(false);

const currentCenter = ref({ ...props.center });
const markerPosition = ref({ ...props.center });

// --- MÉTODOS ---

const emitCoordinates = (lat, lng) => {
  // Si es readOnly o no hay función de seteo, no hacemos nada
  if (props.readOnly || !props.setTextInput) return;

  isInternalChange.value = true;
  props.setTextInput(lat.toFixed(7), lng.toFixed(7));
  
  setTimeout(() => { isInternalChange.value = false; }, 500);
};

const updateMarkerPosition = (event) => {
  if (props.readOnly) return; // Bloqueo de seguridad

  const lat = event.latLng.lat();
  const lng = event.latLng.lng();
  
  markerPosition.value = { lat, lng };
  emitCoordinates(lat, lng);
};

const handleMapClick = (event) => {
  // SI ESTÁ EN MODO LECTURA, IGNORAMOS EL CLICK
  if (props.readOnly) return;

  const lat = event.latLng.lat();
  const lng = event.latLng.lng();

  markerPosition.value = { lat, lng };
  emitCoordinates(lat, lng);
};

const setLocationFromString = (coordsString) => {
  if (!coordsString) return;

  const coordsArray = coordsString.split(",");
  if (coordsArray.length === 2) {
    const lat = parseFloat(coordsArray[0]);
    const lng = parseFloat(coordsArray[1]);

    if (!isNaN(lat) && !isNaN(lng)) {
      const pos = { lat, lng };
      markerPosition.value = pos;
      
      // En modo lectura, siempre centramos en el punto guardado al cargar
      // En modo edición, evitamos saltos si fue por drag
      if (props.readOnly || !isInternalChange.value) {
        currentCenter.value = pos;
      }
    }
  }
};

const getCurrentLocation = () => {
  // Si es solo lectura, NO buscamos la ubicación actual del usuario.
  // Mostramos el mapa centrado en el default o en el punto guardado.
  if (props.readOnly) return;

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!props.positionReceived) {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          currentCenter.value = pos;
          markerPosition.value = pos;
        }
      },
      (error) => {
        console.warn("Ubicación por defecto:", error);
      }
    );
  }
};

// --- LIFECYCLE ---

onMounted(() => {
  if (props.positionReceived) {
    setLocationFromString(props.positionReceived);
  } else {
    getCurrentLocation();
  }
});

// --- WATCHERS ---

watch(() => props.center, (newVal) => {
  // Solo actualizamos centro si no hay una posición fija recibida
  if (newVal && !props.positionReceived) {
    currentCenter.value = { ...newVal };
    if (!props.readOnly) {
        markerPosition.value = { ...newVal };
    }
  }
}, { deep: true });

watch(() => props.positionReceived, (newVal) => {
  if (newVal) {
    setLocationFromString(newVal);
  }
});
</script>

<style scoped>
.mapContenedor {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
}

.mapa {
  width: 100%;
  height: 100%;
  margin: auto;
}

@media (max-width: 1200px) {
  .mapContenedor {
    width: 100%;
  }
  .mapa {
    width: 100%;
  }
}
</style>